import { call, fork, put, takeLatest } from "redux-saga/effects";

import { Meta } from "entities/common";
import {
  AddUserRequest,
  CompanyItem,
  CompanyListStatistics,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  UserItem,
} from "entities/companies";

import { CompaniesApi } from "services/http";
import { getFetchSaga } from "store/commonSagas";
import {
  addCompanyUserFailure,
  addCompanyUserRequest,
  addCompanyUserSuccess,
  createCompanyFailure,
  createCompanyRequest,
  createCompanySuccess,
  getCompanyFailure,
  getCompanyListFailure,
  getCompanyListRequest,
  getCompanyListStatisticsFailure,
  getCompanyListStatisticsRequest,
  getCompanyListStatisticsSuccess,
  getCompanyListSuccess,
  getCompanyRequest,
  getCompanyStatisticsFailure,
  getCompanyStatisticsRequest,
  getCompanyStatisticsSuccess,
  getCompanySuccess,
  getCompanyUsersFailure,
  getCompanyUsersRequest,
  getCompanyUsersSuccess,
  removeCompanyFailure,
  removeCompanyRequest,
  removeCompanySuccess,
  removeCompanyUserFailure,
  removeCompanyUserRequest,
  removeCompanyUserSuccess,
  resendCompanyUserInvitationFailure,
  resendCompanyUserInvitationRequest,
  resendCompanyUserInvitationSuccess,
  updateCompanyFailure,
  updateCompanyRequest,
  updateCompanySuccess,
  updateCompanyUserFailure,
  updateCompanyUserRequest,
  updateCompanyUserSuccess,
} from "store/slices/companiesSlice";
import { showToast } from "store/slices/toastSlice";

function* getList(action: { payload: string }) {
  try {
    const response: { data: { items: CompanyItem[] } & Meta } = yield call(CompaniesApi.getList, action.payload);

    yield put(getCompanyListSuccess(response.data));
  } catch (error) {
    yield put(getCompanyListFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchGetList() {
  yield takeLatest(getCompanyListRequest.type as unknown as Parameters<typeof takeLatest>[0], getList);
}

function* create(action: { payload: CreateCompanyRequest }) {
  try {
    const response: { data: CompanyItem } = yield call(CompaniesApi.create, action.payload);
    yield put(createCompanySuccess(response.data));
  } catch (error) {
    yield put(createCompanyFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchCreate() {
  yield takeLatest(createCompanyRequest.type as unknown as Parameters<typeof takeLatest>[0], create);
}

function* update(action: { payload: { id: number; body: UpdateCompanyRequest } }) {
  try {
    const response: { data: CompanyItem } = yield call(CompaniesApi.update, action.payload);

    yield put(updateCompanySuccess(response.data));
  } catch (error) {
    yield put(updateCompanyFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchUpdate() {
  yield takeLatest(updateCompanyRequest.type as unknown as Parameters<typeof takeLatest>[0], update);
}

function* remove(action: { payload: { id: number } }) {
  try {
    yield call(CompaniesApi.remove, action.payload.id);

    yield put(removeCompanySuccess(action.payload.id));
  } catch (error) {
    yield put(removeCompanyFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchRemove() {
  yield takeLatest(removeCompanyRequest.type as unknown as Parameters<typeof takeLatest>[0], remove);
}

function* getCompany(action: { payload: number }) {
  try {
    const response: { data: CompanyItem } = yield call(CompaniesApi.getCompany, action.payload);

    yield put(getCompanySuccess(response.data));
  } catch (error) {
    yield put(getCompanyFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchGetCompany() {
  yield takeLatest(getCompanyRequest.type as unknown as Parameters<typeof takeLatest>[0], getCompany);
}

function* getStatistics() {
  try {
    const response: { data: CompanyListStatistics } = yield call(CompaniesApi.getCompanyListStatistics);

    yield put(getCompanyListStatisticsSuccess(response.data));
  } catch (error) {
    yield put(getCompanyListStatisticsFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchGetStatistics() {
  yield takeLatest(getCompanyListStatisticsRequest.type as unknown as Parameters<typeof takeLatest>[0], getStatistics);
}

const watchGetCompanyStatistic = getFetchSaga(
  getCompanyStatisticsRequest.type,
  CompaniesApi.getCompanyStatistics,
  getCompanyStatisticsSuccess,
  getCompanyStatisticsFailure,
);

function* getUsers(action: { payload: { companyId: number; query: string } }) {
  try {
    const response: { data: { items: UserItem[] } & Meta } = yield call(CompaniesApi.getCompanyUsers, action.payload);

    yield put(getCompanyUsersSuccess(response.data));
  } catch (error) {
    yield put(getCompanyUsersFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchGetUsers() {
  yield takeLatest(getCompanyUsersRequest.type as unknown as Parameters<typeof takeLatest>[0], getUsers);
}

function* addUser(action: { payload: { companyId: number; body: AddUserRequest } }) {
  try {
    const response: { data: UserItem } = yield call(CompaniesApi.addUser, action.payload);

    yield put(addCompanyUserSuccess(response.data));
  } catch (error) {
    yield put(addCompanyUserFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchAddUser() {
  yield takeLatest(addCompanyUserRequest.type as unknown as Parameters<typeof takeLatest>[0], addUser);
}

function* updateUser(action: { payload: { companyId: number; userId: number; body: AddUserRequest } }) {
  try {
    const response: { data: UserItem } = yield call(CompaniesApi.updateUser, action.payload);
    yield put(updateCompanyUserSuccess(response.data));
  } catch (error) {
    yield put(updateCompanyUserFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchUpdateUser() {
  yield takeLatest(updateCompanyUserRequest.type as unknown as Parameters<typeof takeLatest>[0], updateUser);
}

function* removeUser(action: { payload: { companyId: number; userId: number } }) {
  try {
    yield call(CompaniesApi.removeUser, action.payload);

    yield put(removeCompanyUserSuccess(action.payload.userId));
  } catch (error) {
    yield put(removeCompanyUserFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchRemoveUser() {
  yield takeLatest(removeCompanyUserRequest.type as unknown as Parameters<typeof takeLatest>[0], removeUser);
}

function* resendUserInvitation(action: { payload: { companyId: number; userId: number } }) {
  try {
    yield call(CompaniesApi.resendUserInvitation, action.payload);

    yield put(
      showToast({
        id: "invitation-resent",
        message: "The invitation was successfully resent.",
        type: "success",
        duration: 3000,
      }),
    );

    yield put(resendCompanyUserInvitationSuccess());
  } catch (error) {
    yield put(
      showToast({
        id: "invitation-resent-error",
        message: "Failed to resent the invitation. Please try again later",
        type: "error",
        duration: 3000,
      }),
    );
    yield put(resendCompanyUserInvitationFailure(error as { detail: Record<string, string> }));
  }
}

export function* watchResendUserInvitation() {
  yield takeLatest(
    resendCompanyUserInvitationRequest.type as unknown as Parameters<typeof takeLatest>[0],
    resendUserInvitation,
  );
}

export const companiesWatchers = [
  fork(watchGetList),
  fork(watchCreate),
  fork(watchUpdate),
  fork(watchRemove),
  fork(watchGetCompany),
  fork(watchGetStatistics),
  fork(watchGetCompanyStatistic),
  fork(watchGetUsers),
  fork(watchAddUser),
  fork(watchUpdateUser),
  fork(watchRemoveUser),
  fork(watchResendUserInvitation),
];
